import React, { useState, useEffect } from 'react';
import { losApi } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import PickSelect from '../../shared/PickSelect';
import { ModuleHeader, TabBtn, Fld, SelectFld } from '../shared/formFields';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Send, FileCheck2 } from 'lucide-react';
import { formatDate, formatDateTime } from '../../kit/time';
import { SendToBorrowerModal } from '../../income-calculator/SendToBorrowerModal';

const NEED_STATUS_OPTIONS = [
  { value: 'requested', label: 'Requested' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'rejected', label: 'Rejected' },
];

// Requests a staff member can email or text to the borrower: borrower-side rows
// that are still open and not sent yet.
const sendable = (r) => ['borrower', 'coborrower'].includes(r.assigned_to) && r.status === 'requested' && !r.sent_at;

// A signing request still waiting on the signer: not signed, link not turned off.
const awaitingSignature = (r) => r.status !== 'signed' && !r.revoked_at;

export function ClientNeedsModule({ loan, onChange }) {
  const [tab, setTab] = useState('docs');
  const [reqs, setReqs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState([]); // doc_request_ids ticked in the list
  const [sigs, setSigs] = useState([]);
  const [sigsFailed, setSigsFailed] = useState(false);
  const load = () => losApi.listDocRequests(loan.loan_file_id).then(setReqs);
  useEffect(() => { load(); }, [loan.loan_file_id]);
  const loadSigs = () => Promise.resolve(losApi.listSignatureRequests?.(loan.loan_file_id))
    .then((rows) => { setSigs(rows || []); setSigsFailed(false); })
    .catch(() => setSigsFailed(true));
  useEffect(() => { loadSigs(); }, [loan.loan_file_id]);
  const setStatus = async (rid, status) => {
    try { await losApi.updateDocRequest(rid, { status }); load(); }
    catch (e) { toast({ title: 'Could not update the request', description: errorMessage(e) }); }
  };

  // Ticked rows go out; with none ticked, every row that is still unsent does.
  // A row that was sent or answered since it was ticked drops out of the pick;
  // the button count always shows what will go.
  const sendableAll = reqs.filter(sendable);
  const chosen = sendableAll.filter((r) => picked.includes(r.doc_request_id));
  const toSend = chosen.length ? chosen : sendableAll;
  const togglePicked = (rid) => setPicked((p) => (p.includes(rid) ? p.filter((x) => x !== rid) : [...p, rid]));

  const statusColor = (r) => ({
    requested: r.sent_at
      ? { bg: '#e0f2fe', color: '#0369a1', label: 'SENT' }
      : { bg: '#f3f4f6', color: '#64748b', label: 'NOT SENT' },
    uploaded: { bg: '#fef3c7', color: '#b45309', label: 'AWAITING REVIEW' },
    reviewed: { bg: '#dcfce7', color: '#166534', label: 'APPROVED' },
    rejected: { bg: '#fee2e2', color: '#b91c1c', label: 'REJECTED' },
  }[r.status] || { bg: '#f3f4f6', color: '#64748b', label: r.status });

  return (
    <div className="space-y-4">
      <ModuleHeader
        title="Client Needs"
        extraButtons={
          <>
            <button onClick={() => setSending(true)} disabled={toSend.length === 0} data-testid="send-doc-requests-btn" className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 bg-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ color: '#070B5F' }}>
              <Send size={12} /> Send to borrower{toSend.length ? ` (${toSend.length})` : ''}
            </button>
            <button onClick={() => setAdding(true)} data-testid="add-doc-request-btn" className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: '#070B5F' }}>
              <Plus size={12} /> Add a request
            </button>
          </>
        }
      />
      {sending && (
        <SendToBorrowerModal
          loan={loan}
          requests={toSend}
          onClose={() => setSending(false)}
          onSent={() => { setPicked([]); load(); onChange?.(); }}
        />
      )}
      {adding && (
        <NewDocRequestModal
          loanId={loan.loan_file_id}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); setTab('docs'); load(); onChange?.(); }}
        />
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex items-center border-b border-slate-100 px-2">
          <TabBtn active={tab === 'docs'} onClick={() => setTab('docs')}>Docs Requested ({reqs.length})</TabBtn>
          <TabBtn active={tab === 'sig'} onClick={() => setTab('sig')}>Getting Signatures ({sigs.filter(awaitingSignature).length})</TabBtn>
        </div>
        {tab === 'docs' && (
          <div className="divide-y divide-slate-100">
            {reqs.length === 0 && <div className="p-8 text-center text-slate-400 text-sm italic">No document requests yet.</div>}
            {reqs.map((r) => {
              const sc = statusColor(r);
              return (
                <div key={r.doc_request_id} className="p-4 flex items-start gap-3 hover:bg-slate-50/60" data-testid={`client-need-${r.doc_type}`}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={picked.includes(r.doc_request_id) && sendable(r)}
                    disabled={!sendable(r)}
                    onChange={() => togglePicked(r.doc_request_id)}
                    title={sendable(r) ? 'Pick to send to the borrower' : 'Already sent, or not waiting on the borrower'}
                    aria-label={`Select ${r.label}`}
                    data-testid={`client-need-pick-${r.doc_request_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[13px]" style={{ color: '#030524' }}>{r.label}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Assigned to {r.assigned_to} · {r.doc_type}
                      {r.sent_at && ` · Sent ${formatDate(r.sent_at)} by ${(r.sent_via || []).map((c) => (c === 'sms' ? 'text' : c)).join(' and ') || 'link'}`}
                    </div>
                  </div>
                  <div className="w-32">
                    <PickSelect
                      label="Status"
                      labelClassName="sr-only"
                      value={r.status}
                      onChange={(v) => setStatus(r.doc_request_id, v)}
                      options={NEED_STATUS_OPTIONS}
                      testId={`client-need-status-${r.doc_request_id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {tab === 'sig' && (
          <div className="divide-y divide-slate-100" data-testid="client-needs-signatures">
            {sigsFailed && (
              <div className="p-8 text-center text-sm text-slate-500">
                We could not load the signature requests.{' '}
                <button onClick={loadSigs} className="font-semibold underline" data-testid="signatures-retry">Retry</button>
              </div>
            )}
            {!sigsFailed && sigs.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm italic">
                No signature requests yet. Send disclosure packages from the Disclosure Forms module.
              </div>
            )}
            {!sigsFailed && sigs.map((r) => {
              const signed = r.status === 'signed';
              const off = !signed && !!r.revoked_at;
              return (
                <div key={r.signature_request_id} className="p-4 flex items-center gap-3" data-testid={`client-sig-${r.signature_request_id}`}>
                  <FileCheck2 size={14} style={{ color: signed ? '#6BA82E' : '#f59e0b' }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px]" style={{ color: '#030524' }}>{r.signer_name} <span className="text-slate-400 font-normal">· {r.signer_email}</span></div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {r.forms?.length || 0} form{(r.forms?.length || 0) !== 1 ? 's' : ''} · {r.emailed_at ? `Emailed ${formatDateTime(r.emailed_at)}` : 'Not emailed'}
                      {signed && r.signed_at && ` · Signed ${formatDateTime(r.signed_at)}`}
                    </div>
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={signed ? { background: '#dcfce7', color: '#166534' } : off ? { background: '#f1f5f9', color: '#475569' } : { background: '#fef3c7', color: '#b45309' }}>
                    {off ? 'turned off' : signed ? 'signed' : 'awaiting signature'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Adds a document to the checklist. Nothing is sent to the borrower from here.
function NewDocRequestModal({ loanId, onClose, onSaved }) {
  const [f, setF] = useState({ label: '', instructions: '', assigned_to: 'borrower', required: true });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.label.trim()) return;
    setSaving(true);
    try {
      await losApi.addDocRequest(loanId, {
        doc_type: 'custom',
        label: f.label.trim(),
        instructions: f.instructions.trim() || null,
        assigned_to: f.assigned_to,
        required: f.required,
      });
      onSaved();
    } catch (e) {
      toast({ title: 'Could not add the request', description: errorMessage(e) });
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle className="font-serif text-lg font-bold mb-4" style={{ color: '#030524' }}>Add a request</DialogTitle>
        <Fld label="Document" value={f.label} onChange={(v) => setF((x) => ({ ...x, label: v.slice(0, 200) }))} placeholder="Divorce decree" testId="doc-request-label" />
        <div className="mt-3"><Label className="text-xs">Instructions</Label>
          <Textarea rows={3} value={f.instructions} maxLength={2000} placeholder="All pages, signed" onChange={(e) => setF((x) => ({ ...x, instructions: e.target.value }))} className="mt-1" data-testid="doc-request-instructions" /></div>
        <div className="grid grid-cols-2 gap-3 mt-3 items-end">
          <SelectFld label="Assigned to" value={f.assigned_to} onChange={(v) => setF((x) => ({ ...x, assigned_to: v }))}
            options={[['borrower', 'Borrower'], ['coborrower', 'Co-borrower']]} />
          <label className="text-[12px] text-slate-600 inline-flex items-center gap-2 h-9 cursor-pointer">
            <Switch checked={f.required} onCheckedChange={(v) => setF((x) => ({ ...x, required: v }))} data-testid="doc-request-required" aria-label="Required" />
            Required
          </label>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving || !f.label.trim()} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: '#070B5F' }} data-testid="save-doc-request">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
